import React from 'react';
import PageShell from '../components/PageShell';
import SectionTitle from '../components/SectionTitle';
import PageSection from '../components/page/PageSection';
import FaqList from '../components/page/FaqList';
import InlineCta from '../components/page/InlineCta';
import StepRow from '../components/page/StepRow';
import Icon from '../components/Icon';
import { tie } from '../components/page/typography';
import {
  estimateSteps, pricingFactors, formats, principles, clientBenefit, pricingFaq
} from '../content/pricing';

/**
 * Цінова політика — окрема сторінка.
 *
 * Раніше це була секція головної без власної адреси: у пошуку її не існувало,
 * поділитися нею окремо було неможливо, а пункт меню «Цінова політика»
 * прокручував головну замість того, щоб кудись вести.
 *
 * Порядок блоків — від питання, з яким сюди приходять: «коли я дізнаюся
 * ціну» (кроки), «від чого вона залежить», «як можна платити», і лише потім
 * принципи й питання. Раніше під банером стояли три абзаци вступу, і кожен
 * повторював одну з відповідей унизу сторінки, а між розділами — стокове
 * фото на пів екрана й другий градієнтний банер, схожий на кінцевий заклик.
 */

/**
 * Від чого залежить вартість: пояснення з прикладом ліворуч, п’ять чинників
 * праворуч.
 *
 * Примітка «саме ці п’ять пунктів…» — підсумком у самій картці чинників.
 * Окремою плашкою під текстом ліворуч вона виглядала загубленою: далеко від
 * переліку, про який говорить, і ще одна сіра панель серед інших. Іконка
 * примітки стоїть у колонці іконок, а текст — по лінії назв чинників.
 */
const CostFactors = () => (
  <section id="faktory" aria-labelledby="faktory-title" className="scroll-mt-28 mt-14 lg:mt-20">
    <div className="grid gap-8 lg:grid-cols-12 lg:gap-12 lg:items-start">
      <div className="lg:col-span-5 lg:self-center">
        <SectionTitle id="faktory-title" caption="З чого складається" title={pricingFactors.title} />
        <p className="text-lg lg:text-xl leading-relaxed text-slate-800 text-balance">{tie(pricingFactors.lead)}</p>
        <p className="mt-4 leading-relaxed text-slate-600">{tie(pricingFactors.example)}</p>
      </div>

      <div className="lg:col-span-7 rounded-2xl border border-slate-200 bg-white px-5 py-2 sm:px-7 lg:px-8 lg:py-3">
        <ul className="list-none m-0 p-0 divide-y divide-slate-100">
          {pricingFactors.items.map((item) => (
            <li key={item.title} className="flex items-start gap-4 py-4">
              <span
                className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"
                aria-hidden="true"
              >
                <Icon name={item.icon} />
              </span>
              <span className="min-w-0 pt-0.5">
                <span className="block font-semibold font-display text-slate-900">{item.title}</span>
                <span className="block mt-0.5 leading-relaxed text-slate-600">{tie(item.text)}</span>
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-2 pt-4 pb-3 border-t border-slate-100 flex items-start gap-4 text-sm leading-relaxed text-slate-600">
          <span className="flex-shrink-0 w-10 flex justify-center" aria-hidden="true">
            <Icon name="fa-comment-dots" className="mt-1 text-teal-700" />
          </span>
          <span className="min-w-0">{tie(pricingFactors.note)}</span>
        </p>
      </div>
    </div>
  </section>
);

/**
 * Формати — картками в ряд, як тарифи: там, де на сторінках цін зазвичай
 * стоїть сума, тут сказано, як формат рахується.
 *
 * Від lg картки ділять рядки спільної сітки (subgrid): «Абонентське
 * обслуговування» переноситься на два рядки, і без цього «Як рахується» та
 * «Підходить, коли…» в сусідніх картках стояли б на різній висоті, а
 * порівнюють їх саме поглядом упоперек.
 *
 * Іконки тут і в чинниках звірені з «Про компанію»: мішень, щит, пазл і око
 * там означають цінності, тож на цій сторінці їх немає. Абонентське
 * обслуговування — картка-абонемент, пакет — схема з кількох напрямків.
 */
const FormatCards = () => (
  <PageSection
    id="formaty"
    caption="Як можна працювати"
    title="Формати співпраці"
    link={{ to: '/poslugy', label: 'Перелік послуг' }}
  >
    <ul className="list-none m-0 p-0 grid gap-5 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-0">
      {formats.map((f) => (
        <li
          key={f.title}
          className="flex flex-col lg:grid lg:grid-rows-subgrid lg:row-span-4 rounded-2xl border border-slate-200 bg-white p-6 lg:p-7"
        >
          <div className="flex items-center gap-4">
            <span
              className="flex-shrink-0 w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"
              aria-hidden="true"
            >
              <Icon name={f.icon} className="text-lg" />
            </span>
            <h3 className="text-lg lg:text-xl font-bold font-display text-slate-900 leading-snug text-balance">
              {f.title}
            </h3>
          </div>

          <div className="mt-5 lg:mt-6">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">Як рахується</p>
            <p className="mt-1 text-lg font-bold font-display text-teal-800 leading-snug">{f.pricing}</p>
          </div>

          <p className="mt-3 leading-relaxed text-slate-600">{tie(f.text)}</p>

          <p className="mt-5 pt-4 lg:mt-6 lg:pt-5 border-t border-slate-200 flex items-start gap-3 text-sm leading-relaxed text-slate-700">
            <Icon name="fa-circle-check" className="mt-0.5 flex-shrink-0 text-teal-700" />
            {tie(f.fit)}
          </p>
        </li>
      ))}
    </ul>
  </PageSection>
);

/**
 * Принципи — без карток. Картки з іконкою тут виглядали точнісінько як усе
 * інше на сторінці, а «Перевагу для клієнта» винесли в окремий градієнтний
 * банер, і разом із кінцевим закликом на сторінці стояли два однакові
 * банери. Тепер перевага — теза розділу під заголовком, а принципи —
 * рядками праворуч: дві колонки близької висоти.
 */
const Principles = () => (
  <section id="pryntsypy" aria-labelledby="pryntsypy-title" className="scroll-mt-28 mt-14 lg:mt-20">
    <div className="grid gap-8 lg:grid-cols-12 lg:gap-12 lg:items-start">
      <div className="lg:col-span-5">
        <SectionTitle id="pryntsypy-title" caption="На чому тримається" title="Три принципи" />
        <div className="flex items-start gap-4 rounded-2xl bg-slate-50 border border-slate-100 p-5 sm:p-6">
          <Icon name="fa-circle-check" className="mt-0.5 flex-shrink-0 text-xl text-teal-700" />
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-teal-700">Перевага для клієнта</p>
            <p className="mt-1.5 text-base lg:text-lg leading-relaxed text-slate-800">{tie(clientBenefit)}</p>
          </div>
        </div>
      </div>

      <ul className="lg:col-span-7 list-none m-0 p-0 divide-y divide-slate-200 border-y border-slate-200">
        {principles.map((p) => (
          <li key={p.title} className="py-5 lg:py-6 grid gap-1.5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-6">
            <h3 className="text-lg font-bold font-display text-slate-900">{p.title}</h3>
            <p className="leading-relaxed text-slate-600">{tie(p.text)}</p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const PricingPage = () => {
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: pricingFaq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  };

  return (
    <PageShell
      title="Вартість юридичного супроводу клініки"
      description="Як формується вартість юридичних послуг для медичного бізнесу: принципи ціноутворення, три формати співпраці та відповіді на питання про оплату."
      path="/tsinova-polityka"
      banner="/images/service-outsourcing-85195fd8.jpg"
      schema={faqSchema}
      crumbs={[{ name: 'Цінова політика' }]}
      heading="Цінова політика"
      lead="Ми будуємо співпрацю на довірі, відкритості та чесності. Тому цінова політика тримається на трьох принципах, а вартість ви дізнаєтеся до початку роботи, а не після неї."
      cta={{
        title: 'Порахуємо вартість під вашу ситуацію',
        text: 'Розкажіть, на якому ви етапі й що вже готове, — і ми назвемо перелік робіт, строк і вартість.'
      }}
    >
      <div className="page-container py-10 lg:py-14">
        {/* Кроки — одразу під банером: банер обіцяє, що вартість відома до
            початку роботи, і тут видно, як саме. Останній крок залитий — до
            нього вартість уже названа й погоджена. */}
        <StepRow
          id="rozrakhunok"
          caption="Без сюрпризів"
          title="Починаємо не з прайсу, а з розмови"
          steps={estimateSteps}
          columnsFrom="lg"
          highlightLast
        />
        <CostFactors />
        <FormatCards />

        {/* Після форматів напрошується питання «а який мій?» — і відповідь
            на нього дає розмова. Кінцевий заклик на телефоні ще за кілька
            екранів. */}
        <InlineCta
          source="pricing-formats-mid"
          title="Не впевнені, який формат ваш?"
          text="Досить імені й телефону — решту з’ясуємо в розмові."
        />

        <Principles />

        {/* Питань лише чотири, і відповіді короткі — ховати їх у розгортання
            немає сенсу. Рівний перелік замість сітки 2×2: картки різної
            висоти «рвали» сітку. */}
        <PageSection
          id="pytannya-pro-vartist"
          caption="Оплата"
          title="Питання про вартість"
          link={{ to: '/pytannya', label: 'Усі часті запитання' }}
        >
          <FaqList items={pricingFaq} variant="list" />
        </PageSection>
      </div>
    </PageShell>
  );
};

export default PricingPage;
